import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  BrainCircuit,
  ChevronRight,
  ClipboardList,
  Clock3,
  Gauge,
  Rocket,
  ShieldCheck,
  Sparkles,
  Timer,
  Users,
} from "lucide-react";
import { motion } from "motion/react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { CDNImage } from "~/components/ui/cdn-image";
import { CoachStatusBadge } from "~/components/ui/coach-status-badge";
import { deriveCoachStatuses } from "~/lib/coach-status";
import {
  getPublicFeaturedCoaches,
  type FeaturedCoach,
} from "~/lib/functions/featured-coaches";
import { createBrandMeta } from "~/lib/shared/meta";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["featuredCoaches"],
      queryFn: () => getPublicFeaturedCoaches({ data: { limit: 6 } }),
      staleTime: 1000 * 60 * 5,
    });
  },
  head: () =>
    createBrandMeta({
      title: "Find Your Perfect Coach — Free AI Matching",
      description:
        "Answer 2 quick questions and get matched with verified coaches for your goals. AI-powered, 100% free, under a minute.",
      canonical: "https://dreamcoachmatch.com/discovery",
      ogUrl: "https://dreamcoachmatch.com/discovery",
      ogImage: "https://dreamcoachmatch.com/img/og/discovery-landing.jpg",
      keywords: [
        "coach discovery tool",
        "find a coach",
        "personalized coach matching",
        "life coach quiz",
        "business coach matching",
        "AI coach matching",
      ],
    }),
  component: DiscoveryLandingPage,
});

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const STAGGER_CONTAINER = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
} as const;

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
} as const;

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const howItWorks = [
  {
    title: "Tell us your goals",
    description:
      "Select what you want to work on and describe your situation in your own words.",
    icon: ClipboardList,
  },
  {
    title: "AI finds your matches",
    description:
      "Our matching engine analyzes your goals and context to surface the most relevant coaches.",
    icon: BrainCircuit,
  },
  {
    title: "Meet your coach",
    description:
      "Explore profiles, compare matches, and book a free intro call with confidence.",
    icon: Rocket,
  },
] as const;

const benefits = [
  {
    title: "Save time instantly",
    description:
      "Skip the directories. We surface the right coaches for your specific goals so you can focus on what matters.",
    icon: Clock3,
  },
  {
    title: "Personalized matching",
    description:
      "Our AI weighs your goals, context, and preferences to rank every result — not just keyword search.",
    icon: Gauge,
  },
  {
    title: "Private and secure",
    description:
      "Your answers stay confidential. We only share what you choose with the coaches you connect with.",
    icon: ShieldCheck,
  },
] as const;

const faqs = [
  {
    question: "Is the coach matching free?",
    answer:
      "Yes. Using our matching tool is completely free. You only pay if you choose to book sessions with a coach.",
  },
  {
    question: "How long does the quiz take?",
    answer:
      "Under a minute. Just 2 questions to see your initial matches. You can optionally answer more to refine results.",
  },
  {
    question: "How is this different from a coaching directory?",
    answer:
      "Directories make you scroll endlessly. We use AI to match your goals, personality, and preferences to the right experts — so the first coaches you see are the most relevant.",
  },
  {
    question: "Who are the coaches on Dream Coach Match?",
    answer:
      "We work with verified, professional coaches across career, business, personal development, health, relationships, and more. Every coach on our platform has been reviewed for credentials and experience.",
  },
  {
    question: "What if I don't like my matches?",
    answer:
      "No problem. You can answer additional questions to refine your results, or retake the quiz at any time. The more context you provide, the better the matches.",
  },
] as const;

// ---------------------------------------------------------------------------
// Coach teaser card (non-clickable)
// ---------------------------------------------------------------------------

function CoachTeaserCard({ coach }: { coach: FeaturedCoach }) {
  const statuses = deriveCoachStatuses({
    membershipPlan: coach.membershipPlan,
    isFeaturedCoach: coach.isFeaturedCoach,
    isFoundingCoach: coach.isFoundingCoach,
  });

  return (
    <motion.div
      variants={FADE_UP}
      className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
    >
      <div className="relative size-14 shrink-0">
        {coach.profilePhotoUrl ? (
          <CDNImage
            src={coach.profilePhotoUrl}
            alt={`${coach.firstName} ${coach.lastName}`}
            width={56}
            height={56}
            className="size-14 rounded-full object-cover"
          />
        ) : (
          <div className="size-14 rounded-full bg-gray-200" />
        )}
        {statuses[0] && (
          <div className="absolute -top-1 -right-1">
            <CoachStatusBadge status={statuses[0]} size="sm" showTooltip />
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-900">
          {coach.firstName} {coach.lastName}
        </p>
        {coach.coachingTitle && (
          <p className="truncate text-xs text-gray-500">
            {coach.coachingTitle}
          </p>
        )}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Landing page
// ---------------------------------------------------------------------------

function DiscoveryLandingPage() {
  const { data: coaches = [] } = useQuery({
    queryKey: ["featuredCoaches"],
    queryFn: () => getPublicFeaturedCoaches({ data: { limit: 6 } }),
    staleTime: 1000 * 60 * 5,
  });

  return (
    <div className="bg-white text-gray-900">
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-linear-to-b from-gray-950 via-gray-900 to-gray-950">
        {/* Decorative mesh */}
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          aria-hidden="true"
        >
          <div className="absolute top-0 left-1/4 h-[500px] w-[500px] rounded-full bg-purple-600/20 blur-[120px]" />
          <div className="absolute right-1/4 bottom-0 h-[400px] w-[400px] rounded-full bg-violet-500/15 blur-[100px]" />
        </div>

        <div className="relative mx-auto max-w-5xl px-4 pt-24 pb-20 sm:pt-32 sm:pb-28">
          <motion.div
            className="flex flex-col items-center text-center"
            variants={STAGGER_CONTAINER}
            initial="hidden"
            animate="show"
          >
            <motion.p
              variants={FADE_UP}
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide text-purple-300 backdrop-blur"
            >
              <Sparkles className="size-3.5" />
              AI-Powered Coach Matching
            </motion.p>

            <motion.h1
              variants={FADE_UP}
              className="max-w-3xl font-heading text-4xl leading-tight font-medium tracking-tight text-white sm:text-5xl md:text-6xl"
            >
              Your perfect coach is{" "}
              <span className="bg-linear-to-r from-purple-400 to-violet-300 bg-clip-text text-transparent">
                2 questions
              </span>{" "}
              away
            </motion.h1>

            <motion.p
              variants={FADE_UP}
              className="mt-6 max-w-xl text-lg leading-relaxed text-gray-400"
            >
              Tell us what you need. Our AI matches you with verified coaches
              who specialize in exactly that. Free, private, under a minute.
            </motion.p>

            <motion.div
              variants={FADE_UP}
              className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
            >
              <Link to="/discovery/match">
                <Button
                  size="lg"
                  variant="primary"
                  className="gap-2 px-8 text-base"
                >
                  Find My Coach
                  <ChevronRight className="size-4" />
                </Button>
              </Link>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Timer className="size-3.5" /> Under 1 min
                </span>
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="size-3.5" /> 100% free
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Coach teaser grid ─────────────────────────────────────── */}
      {coaches.length > 0 && (
        <section className="relative -mt-10 px-4">
          <div className="mx-auto max-w-4xl">
            <motion.div
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
              variants={STAGGER_CONTAINER}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              {coaches.map((coach) => (
                <CoachTeaserCard
                  key={`${coach.firstName}-${coach.lastName}`}
                  coach={coach}
                />
              ))}
            </motion.div>
            <p className="mt-4 text-center text-sm text-gray-400">
              <Users className="mr-1.5 inline size-3.5 align-text-bottom" />
              Find your match among our network of verified coaches
            </p>
          </div>
        </section>
      )}

      {/* ── How it works ──────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-4 py-20 sm:py-28">
        <h2 className="text-center font-heading text-3xl font-medium tracking-tight sm:text-4xl">
          How it works
        </h2>
        <p className="mt-3 text-center text-lg text-gray-500">
          Three steps from "I need a coach" to your first session.
        </p>

        <div className="mt-14 grid gap-10 sm:grid-cols-3">
          {howItWorks.map(({ title, description, icon: Icon }, i) => (
            <motion.div
              key={title}
              className="flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="flex size-14 items-center justify-center rounded-2xl bg-purple-50 text-purple-700">
                <Icon className="size-6" />
              </div>
              <h3 className="mt-5 font-heading text-xl font-medium">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-500">
                {description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Why Dream Coach Match ──────────────────────────────────── */}
      <section className="bg-gray-950 py-20 sm:py-28">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-center font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl">
            Why use our coach matching tool?
          </h2>
          <p className="mt-3 text-center text-lg text-gray-400">
            Choosing a coach shouldn't be stressful. Let our AI give you
            confidence from the very first step.
          </p>

          <div className="mt-14 grid gap-8 sm:grid-cols-3">
            {benefits.map(({ title, description, icon: Icon }, i) => (
              <motion.div
                key={title}
                className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-6 text-white backdrop-blur"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex size-14 items-center justify-center rounded-xl bg-white/10 text-purple-200">
                  <Icon className="size-6" />
                </div>
                <h3 className="font-heading text-xl font-medium">{title}</h3>
                <p className="text-sm leading-relaxed text-gray-300">
                  {description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO prose ────────────────────────────────────────────────── */}
      <section className="border-t border-gray-100 py-20 sm:py-28">
        <div className="mx-auto prose max-w-3xl px-4 prose-gray">
          <h2>How to find the right coach for you</h2>
          <p>
            Finding the right coach can be overwhelming — especially when you
            don't know where to start. That's why our coach matching tool was
            created: to simplify the process and guide you to a perfect fit.
          </p>
          <p>
            A great coaching relationship is built on trust, communication, and
            shared goals. Dream Coach Match analyzes these factors so every
            recommendation feels personal. Whether you're looking for career
            coaching, business coaching, personal development, health and
            fitness, relationship support, or stress management — our AI matches
            you with coaches who specialize in exactly what you need.
          </p>
          <p>
            Everyone deserves access to a great coach — someone who can inspire,
            motivate, and guide lasting change. Our discovery experience
            eliminates endless browsing and helps you choose a coach with
            confidence. Answer two quick questions, explore curated matches, and
            start making progress today.
          </p>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────── */}
      <section className="border-t border-gray-100 bg-gray-50/50 py-20 sm:py-28">
        <div className="mx-auto max-w-2xl px-4">
          <h2 className="text-center font-heading text-3xl font-medium tracking-tight sm:text-4xl">
            Common questions
          </h2>

          <Accordion.Root type="single" collapsible className="mt-10 space-y-3">
            {faqs.map(({ question, answer }) => (
              <AccordionItem
                key={question}
                value={question}
                className="rounded-xl border border-gray-200 bg-white px-5"
              >
                <AccordionTrigger className="text-left text-base font-medium text-gray-900 hover:text-purple-700">
                  {question}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-gray-600">
                  {answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion.Root>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────────────── */}
      <section className="bg-gray-950 py-20 sm:py-28">
        <div className="mx-auto max-w-2xl px-4 text-center">
          <h2 className="font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl">
            Ready to meet your coach?
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Two questions. Personalized matches. Zero commitment.
          </p>
          <div className="mt-8">
            <Link to="/discovery/match">
              <Button
                size="lg"
                variant="primary"
                className="gap-2 px-8 text-base"
              >
                Start Matching
                <ChevronRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
